import ky from "ky";
import { network } from "../config";
import TypeDefaultResponse from "../types/TypeDefaultResponse";
import {
  RefoundSession,
  SessionInfoForAdmin,
  SessionPackage,
  Sessions,
  TypeSessionFilters,
} from "../types/TypeSession";
import { TypeUpdatedUserInfo, TypeUserData } from "../types/TypeUsers";
import url from "../utils/formApiUrl";
import customKy from "./kyExtend";

const { sessions } = network;

const baseURL = process.env.REACT_APP_HTTP_CONNECTION_STRING + "sessions/";

const getAuthHeader = (token: string) => {
  return { Authorization: `Bearer ${token}` };
};

export default class SessionsApi {
  static async getSessionsWaitingForRefund(
    token: string,
    page: number,
    limit: number,
  ): Promise<
    TypeDefaultResponse & {
      sessions?: RefoundSession[];
      amount: number;
    }
  > {
    return await ky
      .get(
        url(baseURL, sessions.getSessionsWaitingForRefund, { page, limit }),
        { headers: getAuthHeader(token) },
      )
      .then((res) => res.json());
  }

  static async refundSession(
    token: string,
    payload: {
      userId: string;
      sessionId: string;
    },
  ): Promise<TypeDefaultResponse> {
    return await customKy
      .post(url(baseURL, sessions.refundSession), {
        json: payload,
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async payOutSession(
    token: string,
    payload: {
      userId: string;
      sessionId: string;
    },
  ): Promise<TypeDefaultResponse> {
    return await customKy
      .post(url(baseURL, sessions.payOutSession), {
        json: payload,
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async getPackageFiles(packageId: string): Promise<
    TypeDefaultResponse & {
      packageImages?: {
        _id: string;
        link: string;
        type: string;
        filename: string;
        size: number;
      }[];
      packageVideos?: {
        _id: string;
        link: string;
        type: string;
        filename: string;
        size: number;
        language: string;
      }[];
      packageSessionImages?: {
        previewImage: {
          _id: string;
          link: string;
          type: string;
          filename: string;
          size: number;
        };
        title: string;
        description?: string | undefined;
        datesAndTime: {
          duration: number;
        }[];
        order: number;
      }[];
    }
  > {
    return await customKy
      .get(url(baseURL, sessions.getPackageFiles, { id: packageId }))
      .then((res) => res.json());
  }

  static async getUnapprovedPackages(
    token: string,
    page: number,
    limit: number,
  ): Promise<
    TypeDefaultResponse & {
      packages?: SessionPackage[];
      totalAmount?: number;
    }
  > {
    return await ky
      .get(url(baseURL, sessions.getUnapprovedPackages, { page, limit }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async updatePackage(
    token: string,
    updateData: TypeUpdatedUserInfo,
  ): Promise<
    TypeDefaultResponse & {
      package?: SessionPackage | null;
    }
  > {
    return await customKy
      .patch(url(baseURL, sessions.updatePackage, { id: updateData._id }), {
        headers: getAuthHeader(token),
        json: { updateData },
      })
      .then((res) => res.json());
  }

  static async requestPackageVerification(
    token: string,
    packageId: string,
  ): Promise<
    TypeDefaultResponse & {
      package?: SessionPackage | null;
    }
  > {
    return SessionsApi.updatePackage(token, {
      _id: packageId,
      isApproveRequested: true,
    });
  }

  static async cancelPackageVerification(
    token: string,
    packageId: string,
    reason?: string,
  ): Promise<
    TypeDefaultResponse & {
      package?: SessionPackage | null;
    }
  > {
    return SessionsApi.updatePackage(token, {
      _id: packageId,
      isApproveRequested: false,
      reviewCancelReason: reason,
    });
  }

  static async approvePackageVerification(
    token: string,
    packageId: string,
  ): Promise<
    TypeDefaultResponse & {
      package?: SessionPackage | null;
    }
  > {
    return SessionsApi.updatePackage(token, {
      _id: packageId,
      isApproved: true,
    });
  }

  static async denyPackageVerification(
    token: string,
    packageId: string,
    reason?: string,
  ): Promise<
    TypeDefaultResponse & {
      package?: SessionPackage | null;
    }
  > {
    return SessionsApi.updatePackage(token, {
      _id: packageId,
      reviewDeclined: true,
      isApproveRequested: false,
      reviewCancelReason: reason,
    });
  }

  static async createSessionPackage(
    token: string,
    payload: {
      userId: string;
      specialistId?: string;
      newPackage: Pick<
        SessionPackage,
        "title" | "description" | "packageCost" | "isActive" | "tags"
      > & {
        specializations: string[];
        project?: string | boolean;
        sessions: {
          title: { text: string; language: string }[];
          description?: { text: string; language: string }[];
          datesAndTime: {
            duration: number;
          }[];
          order: number;
        }[];
      };
    },
  ): Promise<
    TypeDefaultResponse & {
      package?: SessionPackage;
    }
  > {
    return await customKy
      .post(url(baseURL, sessions.createSessionPackage), {
        headers: getAuthHeader(token),
        json: payload,
      })
      .then((res) => res.json());
  }

  static async deletePackageSessionFile(
    token: string,
    payload: {
      packageId: string;
      fileId: string;
      fileType: "packageImage" | "packageVideo" | "packageSessionImage";
    },
  ): Promise<
    TypeDefaultResponse & {
      package?: SessionPackage;
    }
  > {
    return await customKy
      .delete(
        url(baseURL, sessions.deletePackageSessionFile, {
          id: payload.packageId,
        }),
        {
          headers: getAuthHeader(token),
          json: payload,
        },
      )
      .then((res) => res.json());
  }

  static async uploadPackageVideoPartially(
    token: string,
    formData: FormData,
    packageId: string,
  ): Promise<TypeDefaultResponse> {
    return await customKy
      .post(
        url(baseURL, sessions.uploadPackageFilePartially, { id: packageId }),
        {
          body: formData,
          headers: getAuthHeader(token),
          timeout: 300000,
        },
      )
      .then((res) => res.json());
  }

  static async finishPackageVideoUpload(
    payload: {
      language: string;
      originalname: string;
      packageId: string;
      returnPackage: boolean;
    },
    token: string,
  ): Promise<
    TypeDefaultResponse & {
      package?: SessionPackage;
    }
  > {
    return await customKy
      .patch(
        url(baseURL, sessions.finishPackageVideoUpload, {
          id: payload.packageId,
        }),
        {
          json: payload,
          headers: getAuthHeader(token),
          timeout: 300000,
        },
      )
      .then((res) => res.json());
  }

  static async deletePackage(
    token: string,
    packageId: string,
  ): Promise<
    TypeDefaultResponse & {
      package?: SessionPackage | null;
    }
  > {
    return await customKy
      .delete(url(baseURL, sessions.deletePackage, { id: packageId }), {
        headers: getAuthHeader(token),
        json: { packageId },
      })
      .then((res) => res.json());
  }

  static async getFilteredSessions(
    token: string,
    filters: TypeSessionFilters,
  ): Promise<
    TypeDefaultResponse & {
      sessions?: (Omit<Sessions, "invitedUsers" | "specialists"> & {
        invitedUsers: TypeUserData[];
        clients: TypeUserData[];
        specialists: TypeUserData[];
        sessionCost: number;
        clientCost: number;
        specialistPay: number;
      })[];
      totalAmount?: number;
    }
  > {
    return await customKy
      .post(url(baseURL, sessions.getFilteredSessions), {
        headers: getAuthHeader(token),
        json: { filters },
      })
      .then((res) => res.json());
  }

  static async getSessionInfoForAdmin(
    token: string,
    sessionId: string,
  ): Promise<
    TypeDefaultResponse & {
      session?: SessionInfoForAdmin;
    }
  > {
    return await ky
      .get(url(baseURL, sessions.getSessionInfoForAdmin, { id: sessionId }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async acceptRealTimeSessionRequest(
    token: string,
    userId: string,
    bookingId: string,
  ): Promise<TypeDefaultResponse & { session?: Sessions }> {
    return await customKy
      .post(url(baseURL, sessions.acceptRealTimeSessionRequest), {
        headers: getAuthHeader(token),
        json: { userId, bookingId },
      })
      .then((res) => res.json());
  }

  static async cancelRealTimeSessionRequest(
    token: string,
    userId: string,
    bookingId: string,
    reason: string,
  ): Promise<TypeDefaultResponse> {
    return await customKy
      .post(url(baseURL, sessions.cancelRealTimeSessionRequest), {
        headers: getAuthHeader(token),
        json: { userId, bookingId, reason },
      })
      .then((res) => res.json());
  }
}
